import React, { useMemo, useState } from "react";
import {
  FieldErrorsImpl,
  Path,
  RegisterOptions,
  Controller,
  Control,
  ControllerRenderProps,
} from "react-hook-form";
import colors from "../../assets/colors";
import Select, { GroupBase, OptionsOrGroups, ThemeConfig } from "react-select";
import useStorage from "../../hooks/useStorage";
import { buildStyles, CircularProgressbar } from "react-circular-progressbar";
import ImageIcon from "./ImageIcon";

type Props<FieldValues, TValue> = {
  containerStyle?: React.CSSProperties;
  formProps: {
    errors: Partial<FieldErrorsImpl<{ [x: string]: any }>>;
    name: Path<FieldValues>;
    options?: Partial<RegisterOptions>;
    control: Control<any, any>;
  };
};

const ControlledFileInput = <FieldValues, TValue>({
  formProps,
  containerStyle,
}: Props<FieldValues, TValue>) => {
  const ControllerRender = ({
    field: { onChange, onBlur, value },
  }: {
    field: ControllerRenderProps<any, Path<FieldValues>>;
  }) => {
    const { selectFile, uploadFile } = useStorage();
    const [selectedFile, setSelectedFile] = useState<File>();
    // const [uploadTask, setUploadTask] = useState<firebase.default.storage.UploadTask>()
    const [uploadProgress, setUploadProgress] = useState<number>();
    const [url, setUrl] = useState<string>(value);

    const onAddFile = async () => {
      const files = await selectFile(false);
      setSelectedFile(files[0]);
      const uploadTask = uploadFile({
        file: files[0],
        path: formProps.name,
        onProgressChanged: setUploadProgress,
      });
      onChange("uploading");
      uploadTask.then((taskSnapshot) =>
        taskSnapshot.ref.getDownloadURL().then((url) => {
          onChange(url);
          setUrl(url);
          setUploadProgress(undefined);
        })
      );
      // setUploadTask(uploadTask)
    };

    // console.log({uploadProgress, url})

    const Progress = () => (
      <div
        style={{
          height: 30,
          width: 30,
          margin: "0 10px",
          position: "relative",
        }}
      >
        <CircularProgressbar
          styles={buildStyles({
            rotation: 0.25,
            strokeLinecap: "butt",
            textSize: "16px",
            pathTransitionDuration: 0.5,
            pathColor: colors.PRIMARY,
            trailColor: colors.PRIMARY + 50,
            backgroundColor: "#3e98c7",
          })}
          strokeWidth={4}
          value={uploadProgress}
        />
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <p style={{ fontSize: 10 }}>{uploadProgress.toFixed(0)}%</p>
        </div>
      </div>
    );

    return (
      <div
        className="hover-opacity hover-scale"
        onClick={onAddFile}
        style={{
          height: 60,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: colors.BLACK_10,
          overflow: "hidden",
          borderRadius: 10,
          cursor: "pointer",
          ...containerStyle,
        }}
      >
        {uploadProgress !== undefined && <Progress />}
        {uploadProgress === undefined && url && (
          <img
            src={url}
            style={{ height: "100%", width: "100%", objectFit: "cover" }}
          />
        )}
        {!url && uploadProgress === undefined && !selectedFile && (
          <ImageIcon
            name="add_image"
            size={16}
            containerStyle={{
              background: colors.PRIMARY + 50,
              padding: 8,
              borderRadius: 20,
            }}
          />
        )}
      </div>
    );
  };

  return (
    <Controller
      name={formProps.name}
      control={formProps.control}
      rules={formProps.options}
      render={ControllerRender}
    />
  );
};

export default ControlledFileInput;
